import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { Box } from '@zillow/constellation-10';
import { AttachmentGuidelines } from '@zg-rentals/satellite-components';
import Layout from '../../components/Layout';
import StringConstants from '../../constants/StringConstants';
import { InboxPageContextProvider } from '../../contexts/InboxPageContextProvider';
import getFolderFromQueryParam from '../../utils/getFolderFromQueryParam';
import getValueFromEnum from '../../utils/getValueFromEnum';
import {
  ReadRepliedStatus,
  RenterProgressStatus,
  useInboxPage_GetConversationInformationByInquiryIdLazyQuery,
} from '../../../src/types/GraphqlTypes';
import { InboxPageMobileView } from '../../../src/types/InboxPageMobileView';
import getTimezoneData from '../../utils/dateTime/getTimezoneData';
import InboxHeader from '../../components/InboxHeader';
import {
  columnWrapperStyles,
  inboxAppLayoutContainerStyles,
  inboxMainContentStyles,
  inboxPageRootStyles,
  pageContentStyles,
  primaryColumnStyles,
  tertiaryColumnStyles,
} from '../../styles/inboxPageStyles';
import ConversationSearchBar from '../../components/ConversationSearchBar';
import ConversationList from '../../components/ConversationList';
import SecondaryColumn from '../../components/SecondaryColumn';
import ConversationNav from '../../components/ConversationNav';
import NotesBar from '../../components/NotesBar';
import MessageList from '../../components/MessageList';
import InboxSidebar from '../../components/InboxSidebar';
import useDevices from '../../hooks/useDevices';
import { emitSlwInboxPageViewEvent, SLW_INBOX_PAGE_VIEW } from '../../constants/clickstreamEvents/pageView';
import useQueryParamsNext from '../../hooks/useQueryParamsNext';
import { inboxPageIsLoadingVariable } from '../../lib/apollo';
import { logApolloError } from '../../utils/errorUtils';
import InboxPageSkeleton from '../../components/InboxPageSkeleton';
import { withRedirectBoundary } from '../../components/RedirectBoundary';
import { useAuth } from '@zg-rentals/auth-comet';
import { ModalType } from '../../types/ModalType';
import useModal from '../../hooks/useModal';

export default withRedirectBoundary(
  () => {
    const { isMobileView } = useDevices();
    const { user } = useAuth();
    const router = useRouter();
    const { activeModal, clearModal, setActiveModal } = useModal();
    const { query } = router;
    const {
      byProperty,
      conversationSlug,
      daysBack,
      folder,
      leadId,
      mobileView,
      readRepliedStatus,
      renterProgressStatus,
    } = query;
    const [listingAlias, conversationId] = conversationSlug || [];
    const { clientTimeZone, isTimeZoneForced } = getTimezoneData();
    const [getConversationInformationQuery] = useInboxPage_GetConversationInformationByInquiryIdLazyQuery();
    const { updateQueryParams } = useQueryParamsNext();

    // The unifiedInquiry email will sometimes append a leadId parameter. When a leadId parameter exists,
    // locate the conversationId and redirect to the appropriate conversation
    useEffect(() => {
      if (!conversationId && leadId) {
        inboxPageIsLoadingVariable(true);
        getConversationInformationQuery({
          variables: {
            inquiryId: leadId.toString(),
          },
          onCompleted: (data) => {
            inboxPageIsLoadingVariable(false);
            const { conversationId, listingAlias } = data.ZRM_getConversationInformationByInquiryId;

            updateQueryParams({
              queryParamValues: {
                conversationSlug: [listingAlias, conversationId],
                leadId: null,
              },
            });
          },
          onError: (error) => {
            inboxPageIsLoadingVariable(false);
            logApolloError(error, {
              details: 'InboxPage - Error locating conversation information by inquiryId',
              query: 'ZRM_getConversationInformationByInquiryId',
              inquiryId: leadId.toString(),
            });
          },
        });
      }
    }, []);

    useEffect(() => {
      emitSlwInboxPageViewEvent(user);
    }, []);

    const enumValuesFromQueryParams = {
      folder: getFolderFromQueryParam(folder?.toString() || ''),
      mobileView: getValueFromEnum(mobileView?.toString(), InboxPageMobileView) || InboxPageMobileView.ConversationList,
      readRepliedStatus:
        getValueFromEnum(readRepliedStatus?.toString(), ReadRepliedStatus) || ReadRepliedStatus.ShowAll,
      renterProgressStatus:
        getValueFromEnum(renterProgressStatus?.toString(), RenterProgressStatus) || RenterProgressStatus.ShowAll,
    };

    const shouldHideConversationListOnMobile =
      enumValuesFromQueryParams.mobileView != InboxPageMobileView.ConversationList;
    const shouldHideMessageListOnMobile = enumValuesFromQueryParams.mobileView != InboxPageMobileView.MessageList;
    const shouldHideRenterProfileOnMobile = enumValuesFromQueryParams.mobileView != InboxPageMobileView.RenterProfile;

    return (
      <Layout
        showFooter={false}
        mainContentStyles={inboxMainContentStyles}
        pageRootStyles={inboxPageRootStyles}
        appLayoutContainerStyles={inboxAppLayoutContainerStyles}
      >
        <Head>
          <title>{StringConstants.INBOX_PAGE_TITLE}</title>
        </Head>
        <Box css={pageContentStyles} data-eventid={SLW_INBOX_PAGE_VIEW} data-eventdescription="Page view">
          {activeModal === ModalType.AttachmentModal && <AttachmentGuidelines onClose={clearModal} />}

          <InboxPageContextProvider
            value={{
              clientTimeZone,
              conversationId,
              daysBack: Number(daysBack) || undefined,
              folder: enumValuesFromQueryParams.folder,
              isByProperty: !!byProperty,
              isTimeZoneForced,
              listingAlias,
              mobileView: enumValuesFromQueryParams.mobileView,
              readRepliedStatus: enumValuesFromQueryParams.readRepliedStatus,
              renterProgressStatus: enumValuesFromQueryParams.renterProgressStatus,
            }}
          >
            <>
              <InboxHeader />

              <Box css={columnWrapperStyles}>
                <Box
                  css={{
                    ...primaryColumnStyles,
                    display: shouldHideConversationListOnMobile ? { base: 'none', xxl: 'flex' } : 'flex',
                  }}
                >
                  <ConversationSearchBar />
                  <ConversationList />
                </Box>

                <SecondaryColumn shouldHideOnMobile={shouldHideMessageListOnMobile}>
                  {!!conversationId && (
                    <>
                      <ConversationNav />
                      {isMobileView && <NotesBar />}
                    </>
                  )}
                  <MessageList />
                </SecondaryColumn>

                <Box
                  css={{
                    ...tertiaryColumnStyles,
                    display: shouldHideRenterProfileOnMobile ? { base: 'none', xxl: 'flex' } : 'flex',
                  }}
                >
                  <InboxSidebar />
                </Box>
              </Box>
            </>
          </InboxPageContextProvider>
        </Box>
      </Layout>
    );
  },
  {
    loading: <InboxPageSkeleton />,
  },
);
